import { useState, useEffect } from 'react';
import { Shield, FileText, Lock, Building, Phone, Mail, MapPin, AlertTriangle, Truck } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import { useTenant } from '../../context/TenantContext.jsx';
import { useContentBlocks, resolveContentValue, CONTENT_FALLBACKS } from '../../lib/content.js';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { EditableText } from '../../components/editing/EditableText.jsx';
import { SanitizedHtml } from '../../lib/sanitizer.jsx';
import { isDemoMode } from '../../lib/demo-mode.js';

export function LegalInfoView({ page = 'impressum', onNavigate }) {
  const { tenant } = useTenant();
  const [activeTab, setActiveTab] = useState(page === 'widerruf' ? 'agb' : (page || 'impressum'));

  const { revision, canEdit, editMode } = useEditMode();
  const { map: legalMap } = useContentBlocks('legal', tenant?.slug, { revision });

  useEffect(() => {
    if (page) {
      if (page === 'widerruf') {
        setActiveTab('agb');
      } else if (['impressum', 'datenschutz', 'agb', 'versand'].includes(page)) {
        setActiveTab(page);
      }
    }
  }, [page]);

  const handleTabChange = (val) => {
    setActiveTab(val);
    if (onNavigate) {
      onNavigate(`/${val}`);
    }
  };

  const address = tenant?.address;
  const companyName = (typeof address === 'object' && address?.company) || tenant?.name || 'ASL Partner-Shop';
  const isDemo = Boolean(isDemoMode(tenant?.settings) || tenant?.id === 'ten_demo_partner' || tenant?.slug === 'demo-partner');

  // Impressum
  const imprintTitleRaw = resolveContentValue(legalMap, 'imprint_title', '');
  const imprintTitle = imprintTitleRaw || CONTENT_FALLBACKS.legal.imprint_title;
  const imprintContentRaw = resolveContentValue(legalMap, 'imprint_content', '');
  const imprintFallback = tenant?.imprint || (isDemo ? CONTENT_FALLBACKS.legal.imprint_content : '');
  const imprintContent = imprintContentRaw || imprintFallback;
  const hasImprint = Boolean(imprintContent && imprintContent.trim().length > 0);
  const isImprintDemo = Boolean(
    hasImprint &&
      (isDemo ||
        imprintContent.includes('Demo-Inhalt') ||
        imprintContent.includes('Demo-Platzhalter') ||
        imprintContent.includes('unverbindlicher Demo-Platzhalter'))
  );

  // Datenschutz
  const privacyTitleRaw = resolveContentValue(legalMap, 'privacy_title', '');
  const privacyTitle = privacyTitleRaw || CONTENT_FALLBACKS.legal.privacy_title;
  const privacyContentRaw = resolveContentValue(legalMap, 'privacy_content', '');
  const privacyFallback = tenant?.privacyPolicy || (isDemo ? CONTENT_FALLBACKS.legal.privacy_content : '');
  const privacyContent = privacyContentRaw || privacyFallback;
  const hasPrivacy = Boolean(privacyContent && privacyContent.trim().length > 0);
  const isPrivacyDemo = Boolean(
    hasPrivacy &&
      (isDemo ||
        privacyContent.includes('Demo-Inhalt') ||
        privacyContent.includes('Demo-Platzhalter') ||
        privacyContent.includes('unverbindlicher Demo-Platzhalter'))
  );

  // AGB & Widerruf
  const termsContent = tenant?.agb || tenant?.terms || tenant?.termsOfService || null;
  const agbTitleRaw = resolveContentValue(legalMap, 'agb_title', '');
  const agbTitle = agbTitleRaw || CONTENT_FALLBACKS.legal.agb_title;
  const agbContentRaw = resolveContentValue(legalMap, 'agb_content', '');
  const agbFallback = termsContent || (isDemo ? CONTENT_FALLBACKS.legal.agb_content : '');
  const agbContent = agbContentRaw || agbFallback;
  const hasAgb = Boolean(agbContent && String(agbContent).trim().length > 0);
  const isAgbDemo = Boolean(
    hasAgb &&
      (isDemo ||
        String(agbContent).includes('Demo-Inhalt') ||
        String(agbContent).includes('Demo-Platzhalter') ||
        String(agbContent).includes('unverbindlicher Demo-Platzhalter'))
  );

  // Versand & Lieferung
  const shippingTitleRaw = resolveContentValue(legalMap, 'shipping_title', '');
  const shippingTitle = shippingTitleRaw || CONTENT_FALLBACKS.legal.shipping_title;
  const shippingContentRaw = resolveContentValue(legalMap, 'shipping_content', '');
  const shippingFallback = isDemo ? CONTENT_FALLBACKS.legal.shipping_content : '';
  const shippingContent = shippingContentRaw || shippingFallback;
  const hasShipping = Boolean(shippingContent && shippingContent.trim().length > 0);
  const isShippingDemo = Boolean(
    hasShipping &&
      (isDemo ||
        shippingContent.includes('Demo-Inhalt') ||
        shippingContent.includes('Demo-Platzhalter') ||
        shippingContent.includes('unverbindlicher Demo-Platzhalter'))
  );

  const renderDemoBanner = () => (
    <div
      data-testid="legal-demo-banner"
      className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 shadow-sm"
    >
      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="space-y-1 text-xs sm:text-sm">
        <p className="font-bold flex items-center gap-1.5">
          <span>Demo-Inhalt — wird vor Kundenkontakt ersetzt</span>
        </p>
        <p className="text-amber-800 leading-relaxed">
          Dies ist ein unverbindlicher Muster-Platzhalter für die Präsentation. Vor dem produktiven Kundenkontakt müssen hier die individuellen Rechtstexte des Partners hinterlegt werden.
        </p>
      </div>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-12 sm:px-6 lg:px-8 space-y-8">
      
      {/* Header */}
      <div className="text-center space-y-2 border-b border-[var(--border)] pb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--foreground)]">
          Rechtliche Informationen
        </h1>
        <p className="text-sm text-[var(--secondary)]">
          Rechtliche Angaben und Pflichtinformationen für {companyName}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex justify-center mb-8">
          <TabsList className="grid w-full max-w-2xl grid-cols-2 sm:grid-cols-4">
            <TabsTrigger value="impressum">Impressum</TabsTrigger>
            <TabsTrigger value="datenschutz">Datenschutz</TabsTrigger>
            <TabsTrigger value="agb">AGB & Widerruf</TabsTrigger>
            <TabsTrigger value="versand">Versand & Lieferung</TabsTrigger>
          </TabsList>
        </div>

        {/* IMPRESSUM */}
        <TabsContent value="impressum" className="space-y-6">
          {isImprintDemo && renderDemoBanner()}
          {hasImprint || (canEdit && editMode) ? (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
                <EditableText
                  page="legal"
                  blockKey="imprint_title"
                  value={imprintTitleRaw}
                  data-testid="edit-imprint-title"
                >
                  <SanitizedHtml content={imprintTitle} keyPrefix="imprint-title" />
                </EditableText>
              </h2>
              <div className="rounded-xl border border-[var(--border)] bg-white p-6 shadow-sm">
                <EditableText
                  page="legal"
                  blockKey="imprint_content"
                  value={imprintContentRaw}
                  multiline={true}
                  tag="div"
                  data-testid="edit-imprint-content"
                  className="w-full text-sm leading-relaxed text-[var(--foreground)]"
                >
                  <SanitizedHtml content={imprintContent} keyPrefix="imprint-content" />
                </EditableText>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] p-8 text-center space-y-2">
              <Building className="mx-auto h-8 w-8 text-[var(--secondary)] opacity-60" />
              <p className="font-display font-semibold text-base text-[var(--foreground)]">
                Kein Impressum hinterlegt
              </p>
              <p className="text-sm text-[var(--secondary)] max-w-md mx-auto">
                Rechtliche Informationen sind für diesen Mandanten derzeit nicht hinterlegt.
              </p>
            </div>
          )}
        </TabsContent>

        {/* DATENSCHUTZ */}
        <TabsContent value="datenschutz" className="space-y-6">
          {isPrivacyDemo && renderDemoBanner()}
          {hasPrivacy || (canEdit && editMode) ? (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
                <EditableText
                  page="legal"
                  blockKey="privacy_title"
                  value={privacyTitleRaw}
                  data-testid="edit-privacy-title"
                >
                  <SanitizedHtml content={privacyTitle} keyPrefix="privacy-title" />
                </EditableText>
              </h2>
              <div className="rounded-xl border border-[var(--border)] bg-white p-6 shadow-sm">
                <EditableText
                  page="legal"
                  blockKey="privacy_content"
                  value={privacyContentRaw}
                  multiline={true}
                  tag="div"
                  data-testid="edit-privacy-content"
                  className="w-full text-sm leading-relaxed text-[var(--foreground)]"
                >
                  <SanitizedHtml content={privacyContent} keyPrefix="privacy-content" />
                </EditableText>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] p-8 text-center space-y-2">
              <Lock className="mx-auto h-8 w-8 text-[var(--secondary)] opacity-60" />
              <p className="font-display font-semibold text-base text-[var(--foreground)]">
                Keine Datenschutzerklärung hinterlegt
              </p>
              <p className="text-sm text-[var(--secondary)] max-w-md mx-auto">
                Datenschutzhinweise sind für diesen Mandanten derzeit nicht hinterlegt.
              </p>
            </div>
          )}
        </TabsContent>

        {/* AGB & WIDERRUF */}
        <TabsContent value="agb" className="space-y-6">
          {isAgbDemo && renderDemoBanner()}
          {hasAgb || (canEdit && editMode) ? (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
                <EditableText
                  page="legal"
                  blockKey="agb_title"
                  value={agbTitleRaw}
                  data-testid="edit-agb-title"
                >
                  <SanitizedHtml content={agbTitle} keyPrefix="agb-title" />
                </EditableText>
              </h2>
              <div className="rounded-xl border border-[var(--border)] bg-white p-6 shadow-sm">
                <EditableText
                  page="legal"
                  blockKey="agb_content"
                  value={agbContentRaw}
                  multiline={true}
                  tag="div"
                  data-testid="edit-agb-content"
                  className="w-full text-sm leading-relaxed text-[var(--foreground)]"
                >
                  <SanitizedHtml content={agbContent} keyPrefix="agb-content" />
                </EditableText>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] p-8 text-center space-y-2">
              <FileText className="mx-auto h-8 w-8 text-[var(--secondary)] opacity-60" />
              <p className="font-display font-semibold text-base text-[var(--foreground)]">
                Keine AGB hinterlegt
              </p>
              <p className="text-sm text-[var(--secondary)] max-w-md mx-auto">
                Allgemeine Geschäftsbedingungen (AGB) sind für diesen Mandanten derzeit nicht hinterlegt.
              </p>
            </div>
          )}
        </TabsContent>

        {/* VERSAND & LIEFERUNG */}
        <TabsContent value="versand" className="space-y-6">
          {isShippingDemo && renderDemoBanner()}
          {hasShipping || (canEdit && editMode) ? (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
                <EditableText
                  page="legal"
                  blockKey="shipping_title"
                  value={shippingTitleRaw}
                  data-testid="edit-shipping-title"
                >
                  <SanitizedHtml content={shippingTitle} keyPrefix="shipping-title" />
                </EditableText>
              </h2>
              <div className="rounded-xl border border-[var(--border)] bg-white p-6 shadow-sm">
                <EditableText
                  page="legal"
                  blockKey="shipping_content"
                  value={shippingContentRaw}
                  multiline={true}
                  tag="div"
                  data-testid="edit-shipping-content"
                  className="w-full text-sm leading-relaxed text-[var(--foreground)]"
                >
                  <SanitizedHtml content={shippingContent} keyPrefix="shipping-content" />
                </EditableText>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] p-8 text-center space-y-2">
              <Truck className="mx-auto h-8 w-8 text-[var(--secondary)] opacity-60" />
              <p className="font-display font-semibold text-base text-[var(--foreground)]">
                Keine Versandinformationen hinterlegt
              </p>
              <p className="text-sm text-[var(--secondary)] max-w-md mx-auto">
                Versand- und Lieferbedingungen sind für diesen Mandanten derzeit nicht hinterlegt.
              </p>
            </div>
          )}
        </TabsContent>

      </Tabs>

    </div>
  );
}
